class FlavorShowcase extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-flavor-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-flavor-dot]'));
    this.liveRegion = this.querySelector('[data-flavor-live]');
    this.stage = this.querySelector('[data-flavor-stage]');
    this.currentIndex = Math.max(
      0,
      this.slides.findIndex((slide) => slide.classList.contains('is-active'))
    );
    this.transitionTimer = undefined;
    this.autoplayTimer = undefined;
    this.pointerStart = undefined;
    this.abortController = new AbortController();

    const { signal } = this.abortController;

    this.querySelector('[data-flavor-previous]')?.addEventListener('click', () => this.move(-1), { signal });
    this.querySelector('[data-flavor-next]')?.addEventListener('click', () => this.move(1), { signal });

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.select(index, index > this.currentIndex ? 1 : -1), { signal });
    });

    this.addEventListener('keydown', this.handleKeydown, { signal });
    this.stage?.addEventListener('pointerdown', this.handlePointerDown, { signal });
    this.stage?.addEventListener('pointerup', this.handlePointerUp, { signal });
    this.stage?.addEventListener('pointercancel', this.clearPointer, { signal });
    this.addEventListener('pointerenter', this.stopAutoplay, { signal });
    this.addEventListener('pointerleave', this.startAutoplay, { signal });
    this.addEventListener('focusin', this.stopAutoplay, { signal });
    this.addEventListener('focusout', this.handleFocusOut, { signal });
    document.addEventListener('visibilitychange', this.handleVisibilityChange, { signal });

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.handleBlockSelect, { signal });
    }

    this.updateControls();
    this.startAutoplay();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    window.clearTimeout(this.transitionTimer);
    window.clearInterval(this.autoplayTimer);
  }

  handleKeydown = (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.move(-1);
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.move(1);
    }
  };

  handlePointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (event.target.closest('a, button')) return;

    this.pointerStart = { x: event.clientX, y: event.clientY, id: event.pointerId };
    this.stage?.setPointerCapture?.(event.pointerId);
    this.stopAutoplay();
  };

  handlePointerUp = (event) => {
    if (!this.pointerStart || this.pointerStart.id !== event.pointerId) return;

    const deltaX = event.clientX - this.pointerStart.x;
    const deltaY = event.clientY - this.pointerStart.y;
    this.clearPointer();

    if (Math.abs(deltaX) >= 48 && Math.abs(deltaX) > Math.abs(deltaY) * 1.2) {
      this.move(deltaX < 0 ? 1 : -1);
    }

    this.startAutoplay();
  };

  clearPointer = () => {
    this.pointerStart = undefined;
  };

  handleFocusOut = (event) => {
    if (!this.contains(event.relatedTarget)) this.startAutoplay();
  };

  handleVisibilityChange = () => {
    document.hidden ? this.stopAutoplay() : this.startAutoplay();
  };

  handleBlockSelect = (event) => {
    const selectedSlide = this.slides.find((slide) => slide.dataset.blockId === event.detail.blockId);
    if (!selectedSlide) return;

    this.stopAutoplay();
    this.select(this.slides.indexOf(selectedSlide), 1, false);
  };

  move(direction) {
    if (this.slides.length < 2) return;
    const nextIndex = (this.currentIndex + direction + this.slides.length) % this.slides.length;
    this.select(nextIndex, direction);
  }

  select(nextIndex, direction = 1, animate = true) {
    if (nextIndex === this.currentIndex || !this.slides[nextIndex]) return;

    window.clearTimeout(this.transitionTimer);
    const outgoing = this.slides[this.currentIndex];
    const incoming = this.slides[nextIndex];

    this.slides.forEach((slide) => slide.classList.remove('is-leaving', 'is-entering'));
    this.dataset.direction = direction > 0 ? 'next' : 'previous';

    outgoing.classList.remove('is-active');
    outgoing.classList.toggle('is-leaving', animate);
    outgoing.setAttribute('aria-hidden', 'true');
    outgoing.inert = true;

    incoming.classList.add('is-active');
    incoming.classList.toggle('is-entering', animate);
    incoming.setAttribute('aria-hidden', 'false');
    incoming.inert = false;

    this.currentIndex = nextIndex;
    this.updateControls();

    if (animate) {
      this.transitionTimer = window.setTimeout(() => {
        outgoing.classList.remove('is-leaving');
        incoming.classList.remove('is-entering');
      }, 850);
    }

    this.restartAutoplay();
  }

  updateControls() {
    this.dots.forEach((dot, index) => {
      const isActive = index === this.currentIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    const activeSlide = this.slides[this.currentIndex];
    if (this.liveRegion && activeSlide) {
      this.liveRegion.textContent = activeSlide.dataset.flavorTitle || '';
    }

    if (activeSlide) {
      const activeTextColor = window.getComputedStyle(activeSlide).getPropertyValue('--flavor-text');
      this.style.setProperty('--active-flavor-text', activeTextColor);
    }
  }

  startAutoplay = () => {
    if (this.dataset.autoplay !== 'true' || this.slides.length < 2 || document.hidden) return;
    if (this.matches(':focus-within') || this.matches(':hover')) return;

    this.stopAutoplay();
    const delay = Number(this.dataset.autoplaySpeed) * 1000 || 6000;
    this.autoplayTimer = window.setInterval(() => this.move(1), delay);
  };

  stopAutoplay = () => {
    window.clearInterval(this.autoplayTimer);
    this.autoplayTimer = undefined;
  };

  restartAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }
}

if (!customElements.get('flavor-showcase')) {
  customElements.define('flavor-showcase', FlavorShowcase);
}
